import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft, MapPin, DownloadSimple, CheckCircle, XCircle, PencilSimple,
  Clock, Receipt, FileText, Briefcase,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import GetDirectionsButton from "../components/GetDirectionsButton";
import PropertyAccessCard from "../components/PropertyAccessCard";
import What3WordsLink from "../components/What3WordsLink";

const STATUS_STYLES = {
  draft:                { label: "Draft",                cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  submitted:            { label: "Submitted",            cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  approved:             { label: "Approved",             cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  sent_to_vendor:       { label: "Sent to You",          cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  issued_to_supplier:   { label: "Issued",               cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  accepted_by_vendor:   { label: "Accepted",             cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  declined_by_vendor:   { label: "Declined",             cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
  changes_requested:    { label: "Changes Requested",    cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  completed:            { label: "Completed",            cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
};

export default function SupplierWorkOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wo, setWO] = useState(null);
  const [loading, setLoading] = useState(true);

  const [responseDialog, setResponseDialog] = useState(null); // "accept" | "decline" | "request_changes"
  const [responseNote, setResponseNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchWO = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/supplier-portal/work-orders/${id}`);
      setWO(data);
    } catch {
      toast.error("Couldn't load Works Order");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchWO(); }, [fetchWO]);

  const downloadPdf = async () => {
    try {
      const response = await API.get(`/works-orders/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${wo.wo_number || "works-order"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download PDF"); }
  };

  const openResponse = (action) => {
    setResponseDialog(action);
    setResponseNote("");
  };

  // One-tap accept — no dialog, no note needed
  const acceptNow = async () => {
    setSubmitting(true);
    try {
      const { data } = await API.post(`/supplier-portal/work-orders/${id}/respond`, { action: "accept", note: "" });
      toast.success(data.message);
      fetchWO();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept");
    } finally { setSubmitting(false); }
  };

  const submitResponse = async () => {
    if (!responseDialog) return;
    if ((responseDialog === "decline" || responseDialog === "request_changes") && !responseNote.trim()) {
      toast.error("Please add a reason / note");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post(`/supplier-portal/work-orders/${id}/respond`, {
        action: responseDialog, note: responseNote.trim(),
      });
      toast.success(data.message);
      setResponseDialog(null);
      setResponseNote("");
      fetchWO();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send response");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!wo) return (
    <div className="p-6">
      <Button variant="ghost" size="sm" onClick={() => navigate("/supplier-portal")} data-testid="supplier-wo-back-btn">
        <ArrowLeft size={14} className="mr-1" /> Back to portal
      </Button>
      <p className="text-sm text-muted-foreground mt-4">Works Order not found.</p>
    </div>
  );

  const responseGiven = wo.supplier_response || "";
  const canRespond = !["accepted", "declined"].includes((responseGiven || "").toLowerCase());
  const statusStyle = STATUS_STYLES[wo.status] || STATUS_STYLES.draft;
  const address = [wo.street_address, wo.town_city, wo.county, wo.postcode].filter(Boolean).join(", ") || wo.property_address;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5" data-testid="supplier-wo-detail">
      {/* Back + Header */}
      <Button variant="ghost" size="sm" onClick={() => navigate("/supplier-portal")} data-testid="supplier-wo-back-btn">
        <ArrowLeft size={14} className="mr-1" /> Back to portal
      </Button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold font-[Chivo]">Works Order</h1>
            <span className="font-mono text-sm text-muted-foreground">{wo.wo_number}</span>
            <Badge className={statusStyle.cls} data-testid="supplier-wo-status-badge">{statusStyle.label}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{wo.scope || wo.job_reference || "—"}</p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="supplier-wo-download-pdf">
          <DownloadSimple size={14} className="mr-1" /> Download PDF
        </Button>
      </div>

      {/* Response banner */}
      {responseGiven ? (
        <ResponseBanner response={responseGiven} note={wo.supplier_response_note} at={wo.supplier_response_at} />
      ) : (
        <div className="border-2 border-dashed border-blue-300 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg p-4" data-testid="supplier-wo-response-panel">
          <div className="flex items-start gap-3">
            <Briefcase size={20} weight="duotone" className="text-blue-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Your response needed</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Review the details below, then accept the works order to confirm, request changes, or decline.
              </p>
            </div>
          </div>
          {canRespond && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
              <Button
                onClick={acceptNow}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="supplier-wo-accept-btn"
              >
                <CheckCircle size={16} weight="fill" className="mr-1" /> {submitting ? "Accepting…" : "Accept"}
              </Button>
              <Button
                variant="outline"
                onClick={() => openResponse("request_changes")}
                className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20"
                data-testid="supplier-wo-changes-btn"
              >
                <PencilSimple size={16} className="mr-1" /> Request Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => openResponse("decline")}
                className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                data-testid="supplier-wo-decline-btn"
              >
                <XCircle size={16} className="mr-1" /> Decline
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Info grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <InfoCard icon={MapPin} label="Property" value={address} action={(wo.latitude || wo.street_address) && (
          <GetDirectionsButton address={address} latitude={wo.latitude} longitude={wo.longitude} variant="ghost" size="sm" data-testid="supplier-wo-directions" />
        )} />
        <InfoCard icon={Clock} label="Due Date" value={wo.due_date || "—"} />
        <InfoCard icon={Receipt} label="Total Budget" value={`£${Number(wo.total_budget || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`} />
        <InfoCard icon={FileText} label="Job Reference" value={wo.job_reference || "—"} />
      </div>

      {wo.what3words && <div className="-mt-2"><What3WordsLink words={wo.what3words} /></div>}
      {wo.job_id && <PropertyAccessCard entityType="job" entityId={wo.job_id} />}

      {/* Scope */}
      {wo.scope && (
        <Section title="Scope of Works"><p className="whitespace-pre-wrap text-sm">{wo.scope}</p></Section>
      )}
      {wo.caveats && (
        <Section title="Caveats & exclusions"><p className="whitespace-pre-wrap text-sm" data-testid="supplier-wo-caveats">{wo.caveats}</p></Section>
      )}
      {wo.site_notes && (
        <Section title="What to look for on site"><p className="whitespace-pre-wrap text-sm" data-testid="supplier-wo-site-notes">{wo.site_notes}</p></Section>
      )}
      {wo.instructions && (
        <Section title="Instructions"><p className="whitespace-pre-wrap text-sm">{wo.instructions}</p></Section>
      )}
      {wo.compliance_requirements && (
        <Section title="Compliance Requirements"><p className="whitespace-pre-wrap text-sm">{wo.compliance_requirements}</p></Section>
      )}

      {/* Line Items */}
      {Array.isArray(wo.line_items) && wo.line_items.length > 0 && (
        <Section title="Line Items">
          <div className="overflow-x-auto" data-testid="supplier-wo-line-items">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-2">#</th>
                  <th className="py-2 px-2">Description</th>
                  <th className="py-2 px-2 text-right">Qty</th>
                  <th className="py-2 px-2">Unit</th>
                  <th className="py-2 px-2 text-right">Unit Price</th>
                  <th className="py-2 pl-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {wo.line_items.map((li, idx) => (
                  <tr key={li.id || idx} className="border-b border-border last:border-0">
                    <td className="py-2 pr-2 text-muted-foreground">{idx + 1}</td>
                    <td className="py-2 px-2">{li.title || li.description || "Item"}</td>
                    <td className="py-2 px-2 text-right">{li.quantity}</td>
                    <td className="py-2 px-2">{li.unit || "ea"}</td>
                    <td className="py-2 px-2 text-right">£{Number(li.unit_price || 0).toFixed(2)}</td>
                    <td className="py-2 pl-2 text-right font-medium">£{Number(li.total || li.budget || 0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan="5" className="py-2 pr-2 text-right">Total:</td>
                  <td className="py-2 pl-2 text-right">£{Number(wo.total_budget || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Response Dialog */}
      <Dialog open={!!responseDialog} onOpenChange={(o) => { if (!o) setResponseDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="supplier-wo-response-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">
              {responseDialog === "accept" && "Accept this Works Order?"}
              {responseDialog === "decline" && "Decline this Works Order"}
              {responseDialog === "request_changes" && "Request Changes"}
            </DialogTitle>
            <DialogDescription>
              {responseDialog === "accept" && "Confirms you'll carry out the work as described. The admin who issued it will be notified."}
              {responseDialog === "decline" && "Let the admin know why you can't take this on. Required."}
              {responseDialog === "request_changes" && "Outline what needs to change (scope, dates, price...). The admin can amend and reissue."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={responseNote}
            onChange={(e) => setResponseNote(e.target.value)}
            placeholder={
              responseDialog === "accept" ? "Optional note — e.g. 'Will start Monday at 9am'."
              : responseDialog === "decline" ? "Reason (required)…"
              : "Describe the changes you need (required)…"
            }
            rows={4}
            data-testid="supplier-wo-response-note"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResponseDialog(null)}>Cancel</Button>
            <Button
              onClick={submitResponse}
              disabled={submitting}
              className={
                responseDialog === "accept" ? "bg-emerald-600 hover:bg-emerald-700" :
                responseDialog === "decline" ? "bg-red-600 hover:bg-red-700" :
                "bg-amber-600 hover:bg-amber-700"
              }
              data-testid="supplier-wo-response-confirm"
            >
              {submitting ? "Sending…" : (
                responseDialog === "accept" ? "Accept Works Order" :
                responseDialog === "decline" ? "Send Decline" :
                "Send Change Request"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, action }) {
  return (
    <div className="border border-border rounded-md p-3 bg-card flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
          {Icon && <Icon size={12} />}
          {label}
        </div>
        <p className="text-sm font-medium mt-0.5 break-words">{value}</p>
      </div>
      {action}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-border rounded-md bg-card p-4">
      <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">{title}</h3>
      {children}
    </div>
  );
}

function ResponseBanner({ response, note, at }) {
  const r = (response || "").toLowerCase();
  const cfg = {
    accepted:          { Icon: CheckCircle, label: "You accepted this Works Order", cls: "border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20", iconCls: "text-emerald-600" },
    declined:          { Icon: XCircle,     label: "You declined this Works Order", cls: "border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/20", iconCls: "text-red-600" },
    changes_requested: { Icon: PencilSimple, label: "Change request sent — awaiting admin", cls: "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20", iconCls: "text-amber-600" },
  }[r];
  if (!cfg) return null;
  const { Icon } = cfg;
  return (
    <div className={`border-2 ${cfg.cls} rounded-lg p-4`} data-testid={`supplier-wo-banner-${r}`}>
      <div className="flex items-start gap-3">
        <Icon size={20} weight="fill" className={`${cfg.iconCls} mt-0.5 shrink-0`} />
        <div className="flex-1">
          <h3 className="font-semibold text-sm">{cfg.label}</h3>
          {note && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">&ldquo;{note}&rdquo;</p>}
          {at && <p className="text-[10px] text-muted-foreground mt-1">{new Date(at).toLocaleString()}</p>}
        </div>
      </div>
    </div>
  );
}
